/**
 * FinanceApprovalDetailPage — Phase 35.9.
 *
 * Full review surface for a single finance approval item.
 * item_type ∈ { report | vat | withholding | period-close }
 * item_id   = report_id | period (YYYY-MM) | close_id
 *
 * F-3: every approve action requires a founder ed25519 signature.
 */

import { useState } from 'react';
import { Navigate, Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { canonicalHash } from '@/api/mock/sign';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import type { ApprovalItemType } from './FinanceApprovalQueuePage';
import { itemTypeLabel, queueItemStatusColor, fmtQueuedAt } from './FinanceApprovalQueuePage';

// ── Types ────────────────────────────────────────────────────────────────────

interface ReportDetail {
  report_id: string;
  report_type: string;
  period_start: string;
  period_end: string;
  data_json: unknown;
  narrative_he: string | null;
  narrative_en: string | null;
  generated_at: string;
  approved: boolean;
}

interface TaxCalcDetail {
  calc_id: string;
  tax_type: string;
  period: string;
  amount_agorot: number;
  calculation: unknown;
  status: string;
  founder_approved_at: string | null;
}

interface PeriodCloseDetail {
  close_id: string;
  period_type: string;
  period: string;
  status: string;
  closed_at: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtIls(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`;
}

function reportTypeHe(t: string): string {
  switch (t) {
    case 'daily':   return 'Daily';
    case 'weekly':  return 'Weekly';
    case 'monthly': return 'Monthly';
    default: return t;
  }
}

// ── Styles ───────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 6,
  padding: '14px 18px',
  minWidth: 160,
};

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: ok ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
      border: `1px solid ${ok ? '#34d399' : '#f87171'}`,
      borderRadius: 6, color: ok ? '#34d399' : '#f87171',
      fontFamily: 'var(--omega-mono)', fontSize: '9pt',
      padding: '10px 20px', zIndex: 9999, direction: 'ltr',
    }}>
      {msg}
    </div>
  );
}

function MetaChip({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)' }}>{value}</span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceApprovalDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { item_type = '', item_id = '' } = useParams<{ item_type: string; item_id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const itype = item_type as ApprovalItemType;

  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Conditional fetches (exactly one enabled per page load) ───────────────

  const reportQuery = useQuery({
    queryKey: ['finance', 'approval-detail', 'report', item_id],
    queryFn: () => apiFetch<ReportDetail>(`/finance/reports/${encodeURIComponent(item_id)}`),
    enabled: itype === 'report' && !!item_id,
    refetchInterval: 60_000,
  });

  const taxQuery = useQuery({
    queryKey: ['finance', 'approval-detail', 'tax', itype, item_id],
    queryFn: () => {
      const ep = itype === 'vat' ? 'vat' : 'withholding';
      return apiFetch<TaxCalcDetail>(`/finance/tax/${ep}?period=${encodeURIComponent(item_id)}`);
    },
    enabled: (itype === 'vat' || itype === 'withholding') && !!item_id,
    refetchInterval: 60_000,
  });

  const closeQuery = useQuery({
    queryKey: ['finance', 'approval-detail', 'period-close', item_id],
    queryFn: async () => {
      const list = await apiFetch<{ closes: PeriodCloseDetail[]; total: number }>('/finance/period-closes');
      const found = list.closes.find(c => c.close_id === item_id);
      if (!found) throw new Error(`close ${item_id} not found`);
      return found;
    },
    enabled: itype === 'period-close' && !!item_id,
    refetchInterval: 60_000,
  });

  // Pick which query is active
  const activeQuery = itype === 'report' ? reportQuery
    : (itype === 'vat' || itype === 'withholding') ? taxQuery
    : closeQuery;

  const isPending = activeQuery.isPending;
  const isError   = activeQuery.isError;

  const report = reportQuery.data;
  const tax    = taxQuery.data;
  const close  = closeQuery.data;

  // Derived display values
  const title_he = itype === 'report' && report
    ? `Report ${reportTypeHe(report.report_type)} — ${report.period_start.slice(0, 7)}`
    : (itype === 'vat' && tax) ? `VAT — ${tax.period}`
    : (itype === 'withholding' && tax) ? `Withholding tax — ${tax.period}`
    : close ? `Period closing — ${close.period}`
    : item_id;

  const status: string = itype === 'report' ? (report?.approved ? 'approved' : 'pending')
    : (itype === 'vat' || itype === 'withholding') ? (tax?.status ?? 'pending')
    : (close?.status ?? 'open');

  const isAlreadyApproved = itype === 'report' ? (report?.approved ?? false)
    : (itype === 'vat' || itype === 'withholding') ? (tax?.status === 'approved')
    : (close?.status === 'approved');

  // ── Approve mutation (F-3) ────────────────────────────────────────────────

  const approveMutation = useMutation({
    mutationFn: async () => {
      const body = { note: null };
      const sig = await signHashWithFounderKey(canonicalHash(body));

      let path: string;
      if (itype === 'report') {
        path = `/finance/reports/${encodeURIComponent(item_id)}/approve`;
      } else if (itype === 'vat' || itype === 'withholding') {
        if (!tax?.calc_id) throw new Error('calc_id not loaded');
        path = `/finance/tax/submission/${encodeURIComponent(tax.calc_id)}/approve`;
      } else {
        if (!close?.period) throw new Error('period not loaded');
        path = `/finance/month-close/${encodeURIComponent(close.period)}/approve`;
      }

      return apiFetch<{ id: string; action: string }>(path, {
        method: 'POST',
        body,
        headers: { 'X-Founder-Signature': sig },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance'] });
      showToast('Item approved successfully', true);
      setTimeout(() => navigate('/omega/finance/approve'), 1500);
    },
    onError: () => showToast('Approval error — check signature and try again', false),
  });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',   to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Approval queue', to: '/omega/finance/approve' },
        { label: title_he.slice(0, 40) },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', direction: 'ltr', maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title_he}
        </h1>
        {!isPending && (
          <span style={{
            fontSize: '7.5pt', padding: '2px 8px',
            border: `1px solid ${queueItemStatusColor(status)}`,
            color: queueItemStatusColor(status),
            letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3,
          }}>
            {status}
          </span>
        )}
        <Link
          to="/omega/finance/approve"
          style={{
            marginLeft: 'auto',
            background: 'rgba(125,211,252,0.12)', border: '1px solid rgba(125,211,252,0.35)',
            borderRadius: 4, color: '#7dd3fc', fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt', fontWeight: 600, padding: '5px 12px', textDecoration: 'none',
          }}
        >
          ← Back to queue
        </Link>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading details.</div>}

      {/* ── Report detail ─────────────────────────────────────────────────── */}
      {itype === 'report' && report && (
        <div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <MetaChip label="Type"      value={`${reportTypeHe(report.report_type)} (${itemTypeLabel('report')})`} />
            <MetaChip label="Period"    value={`${report.period_start.slice(0, 10)} – ${report.period_end.slice(0, 10)}`} />
            <MetaChip label="Generated"     value={fmtQueuedAt(report.generated_at)} />
          </div>
          {report.narrative_he && (
            <div style={{ ...cardStyle, marginBottom: 14, direction: 'ltr' }}>
              <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Narrative</div>
              <div style={{ fontSize: '9.5pt', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{report.narrative_he}</div>
            </div>
          )}
          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Report data (JSON)</div>
            <pre style={{ fontSize: '8pt', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
              {JSON.stringify(report.data_json, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* ── Tax detail ────────────────────────────────────────────────────── */}
      {(itype === 'vat' || itype === 'withholding') && tax && (
        <div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <MetaChip label="Type"    value={itemTypeLabel(itype)} />
            <MetaChip label="Period"  value={tax.period} />
            <MetaChip label="Amount"   value={fmtIls(tax.amount_agorot)} />
            <MetaChip label="calc_id" value={tax.calc_id} />
          </div>
          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Calculation details (JSON)</div>
            <pre style={{ fontSize: '8pt', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
              {JSON.stringify(tax.calculation, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* ── Period-close detail ───────────────────────────────────────────── */}
      {itype === 'period-close' && close && (
        <div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <MetaChip label="Period"     value={close.period} />
            <MetaChip label="Period type" value={close.period_type} />
            {close.closed_at && <MetaChip label="Closed" value={fmtQueuedAt(close.closed_at)} />}
          </div>
        </div>
      )}

      {/* ── Approve action ────────────────────────────────────────────────── */}
      {!isPending && !isError && !isAlreadyApproved && (
        <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.1))' }}>
          <button
            onClick={() => approveMutation.mutate()}
            disabled={approveMutation.isPending}
            style={{
              background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.35)',
              borderRadius: 4, color: '#34d399', fontFamily: 'var(--omega-mono)',
              fontSize: '8.5pt', fontWeight: 700, padding: '7px 18px',
              cursor: approveMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: approveMutation.isPending ? 0.6 : 1,
              direction: 'ltr',
            }}
          >
            {approveMutation.isPending ? 'Approving…' : `Approve — ${itemTypeLabel(itype)}`}
          </button>
          {approveMutation.isError && (
            <div style={{ color: '#f87171', fontSize: '9pt', marginTop: 8, direction: 'ltr' }}>
              Approval error — verify the signature is valid.
            </div>
          )}
        </div>
      )}

      {!isPending && !isError && isAlreadyApproved && (
        <div style={{ marginTop: 24, color: '#34d399', fontSize: '9pt', direction: 'ltr' }}>
          ✓ Item already approved.
        </div>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </div>
  );
}
